import {
    collection,
    doc,
    increment,
    serverTimestamp,
    runTransaction,
    query,
    orderBy,
    onSnapshot,
    type Unsubscribe
} from 'firebase/firestore';
import { convertTimestamps, TIMESTAMP_FIELDS } from '@/utils/firestore';
import { db } from '@/lib/firebase';
import type { Track } from '@/types';

export class TrackService {
    /**
     * Add a track to a playlist
     */
    static async addTrack(playlistId: string, track: Omit<Track, 'addedAt' | 'lastVotedAt'>, userId: string): Promise<void> {
        const trackRef = doc(collection(db, 'playlists', playlistId, 'tracks'), track.id);
        const playlistRef = doc(db, 'playlists', playlistId);

        // Run as transaction to update playlist stats
        await runTransaction(db, async (transaction) => {
            const trackDoc = await transaction.get(trackRef);
            if (trackDoc.exists()) {
                throw new Error('Track already exists in playlist');
            }

            const newTrack: Track = {
                ...track,
                addedBy: userId,
                addedAt: new Date(), // Using client date for immediate optimistic UI, Firestore will enforce types
                score: 0,
                votes: { [userId]: 1 }, // Auto-upvote by adder? Optional. Let's say yes.
                lastVotedAt: new Date()
            };

            // Override with server timestamp for consistency
            const firestoreTrack = {
                ...newTrack,
                addedAt: serverTimestamp(),
                lastVotedAt: serverTimestamp()
            };

            transaction.set(trackRef, firestoreTrack);

            // Update playlist metadata
            transaction.update(playlistRef, {
                'metadata.totalVideos': increment(1),
                updatedAt: serverTimestamp()
            });
        });
    }

    /**
     * Remove a track from a playlist
     */
    static async removeTrack(playlistId: string, trackId: string): Promise<void> {
        const trackRef = doc(db, 'playlists', playlistId, 'tracks', trackId);
        const playlistRef = doc(db, 'playlists', playlistId);
        // Also consider moving to 'removedTracks' subcoll for history - skip for MVP

        await runTransaction(db, async (transaction) => {
            const trackDoc = await transaction.get(trackRef);
            if (!trackDoc.exists()) return; // Already gone

            transaction.delete(trackRef);
            transaction.update(playlistRef, {
                'metadata.totalVideos': increment(-1),
                updatedAt: serverTimestamp()
            });
        });
    }

    /**
     * Vote on a track
     * This is complex: need to handle changing vote (+1 -> -1), removing vote (-1 -> 0), or new vote (0 -> 1)
     */
    static async voteTrack(playlistId: string, trackId: string, userId: string, voteValue: 1 | -1 | 0): Promise<void> {
        const trackRef = doc(db, 'playlists', playlistId, 'tracks', trackId);

        await runTransaction(db, async (transaction) => {
            const trackDoc = await transaction.get(trackRef);
            if (!trackDoc.exists()) throw new Error('Track not found');

            const trackData = trackDoc.data() as Track;
            const currentVote = trackData.votes[userId] || 0;

            if (currentVote === voteValue) return; // No change

            const scoreDiff = voteValue - currentVote;

            // Construct update map
            // Note: Nested field updates in 'votes' map
            // e.g. "votes.userId": voteValue

            const updates: any = {
                score: increment(scoreDiff),
                lastVotedAt: serverTimestamp(),
                [`votes.${userId}`]: voteValue
            };

            if (voteValue === 0) {
                // Usually we might want to delete the key from map, but FieldValue.delete() inside nested map?
                // Firestore supports `deleteField()`
                const { deleteField } = await import('firebase/firestore');
                updates[`votes.${userId}`] = deleteField();
            }

            transaction.update(trackRef, updates);
        });
    }
    static subscribeToTracks(playlistId: string, callback: (tracks: Track[]) => void): Unsubscribe {
        const tracksRef = collection(db, 'playlists', playlistId, 'tracks');
        const q = query(tracksRef, orderBy('score', 'desc'), orderBy('addedAt', 'asc'));

        return onSnapshot(q, (snapshot) => {
            const tracks = snapshot.docs.map(doc =>
                convertTimestamps(doc.data() as Track, TIMESTAMP_FIELDS.track as any)
            );
            callback(tracks);
        });
    }
}
